import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Chip,
  useTheme,
  useMediaQuery 
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  TrendingFlat 
} from '@mui/icons-material';

const KPICard = ({ 
  title, 
  value, 
  change, 
  changeType = 'neutral', // 'positive', 'negative', 'neutral'
  icon: IconComponent,
  color = 'primary',
  loading = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getChangeIcon = () => {
    switch (changeType) {
      case 'positive':
        return <TrendingUp fontSize="small" />;
      case 'negative':
        return <TrendingDown fontSize="small" />;
      default:
        return <TrendingFlat fontSize="small" />;
    }
  };

  const getChangeColor = () => {
    switch (changeType) {
      case 'positive':
        return 'success';
      case 'negative':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatValue = (val) => {
    if (loading) return '...';
    if (val === null || val === undefined || Number.isNaN(val)) return '0';
    if (typeof val === 'number') {
      return val.toLocaleString('he-IL');
    }
    return val;
  };

  return (
    <Card 
      sx={{ 
        height: '100%',
        minHeight: { xs: 120, sm: 140, md: 160 },
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
        position: 'relative',
        overflow: 'visible'
      }}
      elevation={2}
    >
      <CardContent sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        p: { xs: 2, sm: 2.5, md: 3 }
      }}>
        {/* Header with title and icon */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          mb: 1
        }}>
          <Typography 
            variant={isMobile ? 'body2' : 'body1'} 
            color="text.secondary"
            sx={{ 
              fontWeight: 500,
              lineHeight: 1.2
            }}
          >
            {title}
          </Typography>
          {IconComponent && (
            <Box sx={{ 
              color: `${color}.main`,
              opacity: 0.7,
              ml: 1
            }}>
              <IconComponent fontSize={isMobile ? 'small' : 'medium'} />
            </Box>
          )}
        </Box>

        {/* Main value */}
        <Typography 
          variant={isMobile ? 'h4' : 'h3'} 
          color={`${color}.main`}
          sx={{ 
            fontWeight: 'bold',
            mb: 1,
            lineHeight: 1,
            direction: 'ltr',
            textAlign: 'left'
          }}
        >
          {formatValue(value)}
        </Typography>

        {/* Change indicator */}
        {change && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'flex-start'
          }}>
            <Chip
              size="small"
              icon={getChangeIcon()}
              label={change}
              color={getChangeColor()}
              variant="outlined"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                height: { xs: 20, sm: 24 },
                '& .MuiChip-icon': {
                  fontSize: { xs: '0.8rem', sm: '1rem' }
                }
              }}
            />
          </Box>
        )}

        {/* Loading overlay */}
        {loading && (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              טוען...
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KPICard;
